import React, { useState } from "react";
import { DotLegalCheckbox, DotLegalDialog } from "@dotlegal/dotlegal-ui-components";
import { useTranslation } from "../../../localization/useTranslation.ts";
import { DataCategoryViewModel } from "../../DataCategoriesStep/DataCategoriesStep.types.ts";
import { DataType } from "../../../masterData/dataCategory/dataTypes/DataTypesList.types.ts";
import { put } from "../../../common/api/apiShared.ts";
import { DataCategoryDataTypesSaveModel, ProcessingActivityModel } from "../../ProcessingActivity.types.ts";
import { Box } from "@mui/material";
import { isNullOrWhitespace } from "../../../common/stringOperations.ts";
import { useDotLegalQueryClient, useDotLegalMutation } from "@common/hooks/dotLegalReactQuery";

export interface IDataCategoryBoxDialogProps {
    processingActivityId?: string;
    dataSubjectId?: string;
    dataCategory: DataCategoryViewModel;
    allDataTypes: Array<DataType> | undefined;
    onClose: () => void;
    onSave?: (dataCategoryDataTypes: DataCategoryDataTypesSaveModel) => void;
    displayAllDataTypes?: boolean;
}

function DataCategoryBoxDialog(props: IDataCategoryBoxDialogProps) {
    const [selectedDataTypes, setSelectedDataTypes] = useState<Array<string>>(props.dataCategory.selectedDataTypeIds);
    const { translateString } = useTranslation();
    const saveMutation = useDotLegalMutation(save);
    const queryClient = useDotLegalQueryClient();
    const onCheckedChanged = (dataTypeId: string) => {
        const existing = selectedDataTypes.find((s) => s === dataTypeId);
        if (existing) {
            setSelectedDataTypes(selectedDataTypes.filter((s) => s !== dataTypeId));
        } else {
            setSelectedDataTypes([...selectedDataTypes, dataTypeId]);
        }
    };

    function save() {
        if (!isNullOrWhitespace(props.dataSubjectId)) {
            return put<ProcessingActivityModel>(
                `/ProcessingActivity/${props.processingActivityId}/data-categories/${props.dataCategory.id}/data-subjects/${props.dataSubjectId}/data-types`,
                selectedDataTypes
            );
        }

        return put<ProcessingActivityModel>(
            `/ProcessingActivity/${props.processingActivityId}/standardDataCategory/${props.dataCategory.id}/dataTypes`,
            selectedDataTypes
        );
    }

    const getDataTypes = () => {
        return props.dataCategory.dataTypeIds.map((id) => {
            return {
                id: id,
                name: getDataTypeName(id),
            };
        });
    };

    const getDataTypeName = (id: string) => props.allDataTypes?.find((dataType) => dataType.id === id)?.name;
    const dataTypes = getDataTypes().sort((a, b) => a.name!.localeCompare(b.name!));

    const onSave = async () => {
        if (props.onSave) {
            props.onSave({ dataCategoryId: props.dataCategory.id, dataTypeIds: selectedDataTypes });
            props.onClose();
        } else {
            await saveMutation.mutateAsync();
            const queryKey = "processingActivity" + props.processingActivityId!;
            await queryClient.refetchQueries(queryKey);
            props.onClose();
        }
    };

    return (
        <DotLegalDialog
            size={"sm"}
            buttonOkText={translateString("save")}
            open
            onDialogClose={() => props.onClose()}
            onOkClick={onSave}
            header={props.dataCategory.name}
            inProgress={saveMutation.isLoading}
        >
            <Box sx={{ width: 250, margin: "auto" }}>
                {dataTypes?.map((dt) => {
                    return (
                        <DotLegalCheckbox
                            key={dt.id}
                            checked={selectedDataTypes.some((s) => s === dt.id)}
                            label={dt.name}
                            onChange={() => onCheckedChanged(dt.id)}
                        />
                    );
                })}
            </Box>
        </DotLegalDialog>
    );
}

export default DataCategoryBoxDialog;
